import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { liveMetricsData, metricsLabels } from '@/mocks/homepageData';
import { useTheme } from '@/contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

function AnimatedCounter({
  target,
  prefix,
  suffix,
  decimals,
  isInView,
}: {
  target: number;
  prefix: string;
  suffix: string;
  decimals: number;
  isInView: boolean;
}) {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const hasAnimated = useRef(false);

  useEffect(() => {
    if (!isInView || hasAnimated.current) return;
    hasAnimated.current = true;

    const duration = 2000;
    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (target - startValue) * eased;
      countRef.current = current;
      setCount(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isInView, target]);

  // Subtle live update after animation
  useEffect(() => {
    if (!isInView || count === 0) return;
    const interval = setInterval(() => {
      setCount((prev) => prev + (Math.random() - 0.5) * (target * 0.0001));
    }, 3000);
    return () => clearInterval(interval);
  }, [isInView, target, count]);

  const formatNumber = (num: number) => {
    if (decimals > 0) {
      return num.toFixed(decimals);
    }
    if (num >= 1e9) {
      return (num / 1e9).toFixed(2) + 'B';
    }
    if (num >= 1e6) {
      return (num / 1e6).toFixed(2) + 'M';
    }
    if (num >= 1e3) {
      return (num / 1e3).toFixed(1) + 'K';
    }
    return Math.floor(num).toLocaleString();
  };

  return (
    <span className="font-mono">
      {prefix}
      {formatNumber(count)}
      {suffix}
    </span>
  );
}

export default function LiveMetricsSection() {
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const getTrend = (index: number) => {
    const trends = ['+12.4%', '+8.2%', '+24.1%', '+45.6%', '-0.3%', '+67.8%'];
    return trends[index] || '+0.0%';
  };

  const isPositive = (trend: string) => !trend.startsWith('-');

  const textPrimary = isDark ? 'text-white' : 'text-[#111]';
  const textMuted = isDark ? 'text-[#666]' : 'text-[#888]';
  const borderLight = isDark ? 'border-white/[0.06]' : 'border-black/[0.06]';

  return (
    <section
      id="metrics"
      ref={sectionRef}
      className={`relative w-full py-16 md:py-24 border-t ${borderLight}`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-10 md:mb-14"
        >
          <span className="text-xs text-[#00ff88] font-mono uppercase tracking-widest">{t('metrics.realtimeMetrics')}</span>
          <h2 className={`mt-3 text-2xl md:text-3xl lg:text-4xl font-bold ${textPrimary}`}>
            {t('metrics.platformGlance')}
          </h2>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
          {metricsLabels.map((metric, index) => {
            const value = liveMetricsData[metric.key as keyof typeof liveMetricsData];
            const trend = getTrend(index);

            return (
              <motion.div
                key={metric.key}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="glass-card-hover p-4 md:p-5 group"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className={`text-[10px] md:text-xs uppercase tracking-wider font-medium ${textMuted}`}>
                    {t(`metrics.${metric.key}`)}
                  </span>
                  <span
                    className={`text-[10px] font-mono ${
                      isPositive(trend) ? 'text-[#00ff88]' : 'text-[#ff4444]'
                    }`}
                  >
                    {trend}
                  </span>
                </div>
                <div className={`text-xl md:text-2xl lg:text-3xl font-bold group-hover:text-[#00ff88] transition-colors ${textPrimary}`}>
                  <AnimatedCounter
                    target={value as number}
                    prefix={metric.prefix}
                    suffix={metric.suffix}
                    decimals={metric.decimals}
                    isInView={isInView}
                  />
                </div>

                {/* Mini sparkline */}
                <div className="mt-3 h-8 flex items-end gap-px">
                  {Array.from({ length: 12 }).map((_, i) => {
                    const height = 20 + Math.random() * 80;
                    return (
                      <div
                        key={i}
                        className="flex-1 rounded-sm"
                        style={{
                          height: `${height}%`,
                          backgroundColor:
                            i === 11
                              ? isPositive(trend)
                                ? 'rgba(0,255,136,0.6)'
                                : 'rgba(255,68,68,0.6)'
                              : isDark
                                ? 'rgba(255,255,255,0.06)'
                                : 'rgba(0,0,0,0.06)',
                        }}
                      />
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}